/**
 * The pure "what's next" read helper for a project's work-item plan
 * (004b-create-project Phase 5c).
 *
 * Given a project's `work_items` (each carrying a `build_order`) and its
 * `work_item_dependencies` ("blocked by" edges), this derives the BUILD-ORDER-
 * FIRST set of items that are READY TO START — every item whose dependencies are
 * all satisfied — in the order an agent should pick them up. It is the exact read
 * `005-build-project` consumes; surfacing it here keeps producer (004b) and
 * consumer (005) in agreement. There is NO UI in 004b — this is a pure function.
 *
 * Contract:
 *   - An item is READY iff every dependency it has is SATISFIED. A dependency
 *     (`depends_on_work_item_id`) is satisfied when the blocker is DONE — i.e. it
 *     is absent from the pending set (because it was completed and removed, or it
 *     never existed in the input). The caller passes only the items still left to
 *     build; an edge pointing at an id that is not among those items is therefore
 *     already-done and satisfied. (005 reuses this rule.)
 *   - The ready set is ordered by `build_order` first (ascending; null orders
 *     sort last), with a stable tiebreak by `rank` then `id` so the order is
 *     deterministic for equal/absent build orders.
 *   - It DEGRADES GRACEFULLY on empty / degenerate input: no items, items but no
 *     dependencies, or a degenerate graph (e.g. a self-loop, or a 2-cycle the
 *     phase-1 schema allows since multi-hop cycles are not prevented) all return
 *     a well-formed (possibly empty) array and NEVER throw. A self-loop or a
 *     mutual cycle simply leaves those items not-ready (each blocks the other);
 *     resolving multi-hop cycles is out of scope (phase 1's deferral).
 *
 * Pure (no React, no IO) so it is unit-testable in isolation and reusable by 005.
 */

import type { Database } from '../../types/database';

type WorkItemRow = Database['public']['Tables']['work_items']['Row'];
type WorkItemDependencyRow =
  Database['public']['Tables']['work_item_dependencies']['Row'];

/**
 * The minimal shape this helper reads off a `work_items` row: the `id`, its
 * `build_order` (nullable), and `rank` (the stable tiebreak). Accepts a full
 * {@link WorkItemRow} or any object carrying these fields, so callers can pass
 * trimmed rows without widening the type.
 */
export type WhatsNextItem = Pick<WorkItemRow, 'id' | 'build_order' | 'rank'>;

/**
 * The minimal shape this helper reads off a `work_item_dependencies` row: the
 * blocked item (`work_item_id`) and its blocker (`depends_on_work_item_id`).
 */
export type WhatsNextDependency = Pick<
  WorkItemDependencyRow,
  'work_item_id' | 'depends_on_work_item_id'
>;

/**
 * Compare two items by build order first (ascending; null/undefined last), then
 * by `rank` ascending, then by `id` — a total, stable ordering. Exported as THE
 * shared comparator: `buildOrderTree.ts` sorts each sibling group with it, so
 * the per-sibling-group order and the ready-set order can never drift apart.
 */
export function compareByBuildOrder(a: WhatsNextItem, b: WhatsNextItem): number {
  const ao = a.build_order;
  const bo = b.build_order;
  if (ao == null && bo != null) return 1; // null orders sort last
  if (ao != null && bo == null) return -1;
  if (ao != null && bo != null && ao !== bo) return ao - bo;
  if (a.rank !== b.rank) return a.rank - b.rank;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * The build-order-first set of items that are READY TO START — every item whose
 * dependencies are all satisfied — among the given still-to-build `items`.
 *
 * An item is ready iff every `depends_on_work_item_id` edge whose `work_item_id`
 * is this item points at a blocker that is NOT among the still-to-build items (an
 * absent blocker is treated as done/satisfied). Items are returned ordered by
 * `build_order` first (see {@link compareByBuildOrder}). Degenerate input
 * (no items, no deps, self-loops, cycles) returns a well-formed array and never
 * throws.
 */
export function readyToStartItems(
  items: readonly WhatsNextItem[],
  dependencies: readonly WhatsNextDependency[]
): WhatsNextItem[] {
  if (!Array.isArray(items) || items.length === 0) return [];

  // The ids still left to build — the universe against which "satisfied" is
  // judged. A dependency pointing OUTSIDE this set is already-done/satisfied.
  const pendingIds = new Set<string>();
  for (const item of items) {
    if (item && typeof item.id === 'string') pendingIds.add(item.id);
  }

  // For each still-pending item, the set of blockers that are ALSO still pending
  // (an absent / already-done blocker does not block). Defensive against a
  // malformed dependency list (missing ids, edges for unknown items).
  const unmetBlockersByItem = new Map<string, Set<string>>();
  if (Array.isArray(dependencies)) {
    for (const dep of dependencies) {
      if (!dep) continue;
      const blocked = dep.work_item_id;
      const blocker = dep.depends_on_work_item_id;
      if (typeof blocked !== 'string' || typeof blocker !== 'string') continue;
      // Only edges whose BLOCKED item is still pending matter; only blockers that
      // are still pending count as unmet (an already-done blocker is satisfied).
      if (!pendingIds.has(blocked)) continue;
      if (!pendingIds.has(blocker)) continue;
      const set = unmetBlockersByItem.get(blocked) ?? new Set<string>();
      set.add(blocker);
      unmetBlockersByItem.set(blocked, set);
    }
  }

  const ready = items.filter((item) => {
    const unmet = unmetBlockersByItem.get(item.id);
    return unmet === undefined || unmet.size === 0;
  });

  return [...ready].sort(compareByBuildOrder);
}
